/**
 * Shared Apollo cache update functions for community profile mutations.
 *
 * Consolidates the readQuery/writeQuery merge pattern used by both
 * CommunityProfileSettingsPage and CommunityProfileUserView for
 * UPDATE_PROFILE mutations.
 */

import type {
  ApolloCache,
  DefaultContext,
  MutationUpdaterFunction,
  OperationVariables,
  NormalizedCacheObject,
} from '@apollo/client'
import { GET_CURRENT_PROFILE } from '../../query'
import type {
  GetCurrentProfileResponse,
  UpdateProfileResponse,
} from '../../types'

interface ProfileQueryVars {
  language: string
  contentLanguage: string[]
  includeMemberFields: boolean
  includeProfileActivity: boolean
  /**
   * Combined gate `includeMemberFields && includeProfileActivity` — must match
   * what the consumer passed to GET_CURRENT_PROFILE so readQuery/writeQuery
   * hit the same cache key.
   */
  includeWatchHistory: boolean
}

export default function createProfileCacheUpdater (
  queryVars: ProfileQueryVars,
): MutationUpdaterFunction<
  UpdateProfileResponse,
  OperationVariables,
  DefaultContext,
  ApolloCache<NormalizedCacheObject>
> {
  return (cache, { data }) => {
    const updatedProfile = data?.userProfileUpdate?.profile
    if (!updatedProfile) return
    const existing = cache.readQuery<GetCurrentProfileResponse>({
      query: GET_CURRENT_PROFILE,
      variables: queryVars,
    })
    if (!existing) return
    cache.writeQuery({
      query: GET_CURRENT_PROFILE,
      variables: queryVars,
      data: {
        userProfileCurrent: {
          ...(existing.userProfileCurrent || {}),
          ...updatedProfile,
        },
      },
    })
  }
}
